package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerAddressInput
import ai.jessie.shared.customers.CustomerContactInput
import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// UC-012 management for owners and standard members. `customerId` is null on the list page. The
// form and its review open as a slide-over above the list or the record
// (`docs/design/screens/15-Firmen.png`, `16-Firma.png`); the header actions live in the page.
@Composable
fun customerRecords(
    organisationId: String,
    customerId: String?,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    val focusReturn = remember { FocusReturn() }
    customerFocus(state, focusReturn)
    val editing = state.draft != null
    if (!editing) customerMessages(state)
    if (state.error?.authenticationRequired != true)
        customerBody(organisationId, customerId, state, actions)
    if (editing) customerEditor(state, actions)
    if (customerId != null)
        P {
            A(href = "/organisations/$organisationId/customers") { Text("Zurück zur Kundenliste") }
        }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

// While the slide-over is open it shows these itself.
@Composable
private fun customerMessages(state: CustomerUiState) {
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.busy) status("Wird geladen oder gespeichert …")
    state.notice?.let { status(it) }
}

@Composable
private fun customerBody(
    organisationId: String,
    customerId: String?,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    val current = state.current
    when {
        current != null -> {
            customerRecord(current, showName = customerId == null)
            if (customerId == null) savedActions(organisationId, current, state, actions)
            else recordActions(current, state, actions)
        }
        customerId == null -> customerList(organisationId, state, actions)
        else ->
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(RELOAD_CUSTOMER, actions.refresh, disabled = state.busy)
            }
    }
}

// Every view change replaces the control that had focus, so the page title takes it again; while
// a problem is shown, the alert or the marked field keeps focus. The first render leaves the
// frame's own title focus alone.
@Composable
private fun customerFocus(state: CustomerUiState, focusReturn: FocusReturn) {
    LaunchedEffect(state.draft == null, state.current == null) {
        val settled = state.draft == null && state.error == null
        if (focusReturn.pending && settled && !AppGlobals.isExporting)
            (document.getElementById("page-title") as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

@Composable
private fun status(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
private fun savedActions(
    organisationId: String,
    current: CustomerResponse,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    Div(attrs = RowStyle.toAttrs()) {
        linkButton(
            "Kundendatensatz öffnen",
            "/organisations/$organisationId/customers/${current.id}",
            variant = ButtonVariant.PRIMARY,
        )
        themedButton("Zur Kundenliste", actions.close, disabled = state.busy)
    }
}

// Edit, archive and reactivate are header actions of the page; the body keeps the B2 note.
@Composable
private fun recordActions(
    current: CustomerResponse,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    // B2: a staged-delivery limit, not a retention finding about this customer.
    if (current.status == "ACTIVE")
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Endgültiges Entfernen steht in Jessie zurzeit nicht zur Verfügung. " +
                    "Archivieren erhält alle bestehenden Verknüpfungen."
            )
        }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            RELOAD_CUSTOMER,
            actions.refresh,
            variant = ButtonVariant.GHOST,
            disabled = state.busy,
        )
    }
}

/** The page-header actions of the detail page for the returned record's status. */
@Composable
fun customerHeaderActions(current: CustomerResponse, busy: Boolean, actions: CustomerActions) {
    when (current.status) {
        "ACTIVE" -> {
            themedButton("Bearbeiten", actions.edit, disabled = busy)
            themedButton("Kunde archivieren", actions.archive, disabled = busy)
        }
        "ARCHIVED" ->
            themedButton(
                "Angaben prüfen und Kunde reaktivieren",
                actions.reactivate,
                disabled = busy,
            )
    }
}

/** "line1, postalCode city" of the primary address, if the record has one. */
fun CustomerResponse.primaryAddressLine(): String? =
    addresses
        .firstOrNull { it.type == "PRIMARY" }
        ?.let { "${it.line1}, ${it.postalCode} ${it.city}" }

internal fun CustomerAddressInput.summary() =
    "${addressTypes[type] ?: "–"}: " +
        listOfNotNull(line1, line2, "$postalCode $city", countryCode).joinToString(", ")

internal fun CustomerContactInput.summary() =
    listOfNotNull(name, role, email, phone).joinToString(", ") +
        if (billingContact) " · Rechnungskontakt" else ""

internal const val CUSTOMER_TITLE_ID = "customer-title"
internal const val LIST_TITLE = "Firmen und Personen"
private const val RELOAD_CUSTOMER = "Kunde erneut laden"
